import { OrganizationSubscription } from '@manifest-cyber/types/interface/db';

/**
 * Checks if an organization has all the required subscriptions.
 * Handles legacy customers who may have incomplete subscription data.
 * 
 * For legacy customers (undefined, null, or empty objects), defaults to:
 * { sbom: true, aibom: false }
 * 
 * @param subscriptions - The organization's subscription object (can be undefined, empty, or partial)
 * @param requiredSubscriptions - Array of subscription keys that are required
 * @returns true if all required subscriptions are present and enabled, false otherwise
 * 
 * @example
 * // Check for SBOM subscription
 * hasOrganizationSubscriptions(orgSubscriptions, ['sbom'])
 * 
 * @example
 * // Check for multiple subscriptions
 * hasOrganizationSubscriptions(orgSubscriptions, ['sbom', 'aibom'])
 * 
 * @example
 * // Handle legacy empty object (defaults to {sbom: true, aibom: false})
 * hasOrganizationSubscriptions({}, ['sbom']) // returns true
 * hasOrganizationSubscriptions({}, ['aibom']) // returns false
 * 
 * @example
 * // Handle partial object
 * hasOrganizationSubscriptions({sbom: true}, ['aibom']) // returns false
 */
export function hasOrganizationSubscriptions(
  subscriptions: Partial<OrganizationSubscription> | undefined | null,
  requiredSubscriptions: Array<keyof OrganizationSubscription>
): boolean {
  let effectiveSubscriptions: Partial<OrganizationSubscription>;
  const defaultSubscriptions: Partial<OrganizationSubscription> = { sbom: true, aibom: false };

  // If subscriptions are not provided, null, or empty object, set legacy default
  if (!subscriptions || Object.keys(subscriptions).length === 0) {
    effectiveSubscriptions = defaultSubscriptions;
  } else {
    // Handle empty objects or objects with no relevant subscription keys by setting legacy default
    const hasAnySubscriptionKeys = Object.keys(subscriptions).some(key =>
      key === 'sbom' || key === 'aibom'
    );

    if (!hasAnySubscriptionKeys) {
      effectiveSubscriptions = defaultSubscriptions;
    } else {
      effectiveSubscriptions = subscriptions;
    }
  }

  // If no required subscriptions, return true
  if (requiredSubscriptions.length === 0) {
    return true;
  }

  // Check each required subscription - must be explicitly true
  return requiredSubscriptions.every((sub) => {
    return effectiveSubscriptions[sub] === true;
  });
}

export default hasOrganizationSubscriptions;
